import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import SmallPopupWithInput from "../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../components/_utils/CommonToast";
import { formatAccountNumber } from "../../../../components/_utils/FormatAccountNumber";
import UniversalRefreshToast from "../../../../components/_utils/UniversalRefreshToast";
import {
  approveBeneficiaryAccount,
  beneficiaryAccountDetailsById,
  rejectBeneficiaryAccount,
} from "../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import {
  bankLookUp,
  panLookUp,
} from "../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import globalText from "../../../../utils/globalText";
import { linkIdentifier } from "../../../../utils/linkIdentifier";

const BeneficiaryAccountSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const isActionInProgress = isApproving || isRejecting;
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    categoryIds: [],
    pan: "",
    labels: "",
  });

  const services = useSelector((state) => state.refreshToken.services);

  const { beneficiaryAccountDetailsData } = useSelector(
    (state) => state.beneficiaryAccount,
  );
  const { panLookupData, bankLookupData } = useSelector(
    (state) => state.sharedResources,
  );
  const beneAccountSuccessData =
    beneficiaryAccountDetailsData?.beneficiary_accounts[0];

  const canApprove =
    beneAccountSuccessData?.approval_status === "pending" &&
    services?.includes("BAAccount");

  const canCreateTxn = services?.includes("CBeneficiaryTxn");

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  const handleInputChange = (event) => {
    let value = event?.target?.value;
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(0, 150);
    setDeclineReason(value);
  };

  const handlers = {
    createAccount: () =>
      navigate("/beneficiary_account_dashboard/create_beneficiary_account", {
        replace: true,
      }),
    createTxn: () => {
      if (!services?.includes("CBeneficiaryTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "CBeneficiaryTxn" },
        });
        return;
      }

      navigate(
        "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
        {
          state: currentAccountDetails,
          replace: true,
        },
        sessionStorage.removeItem("recreate_ben"),
      );
    },
    viewDetails: () =>
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () =>
      navigate("/beneficiary_account_dashboard", {
        replace: true,
      }),
  };

  const accountApproval = async () => {
    if (isActionInProgress) return;
    try {
      setIsApproving(true);
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");

      const response = await dispatch(approveBeneficiaryAccount({ id }));

      if (approveBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account approved.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsApproving(false);
    }
  };

  const accountRejected = async () => {
    if (isActionInProgress) return;
    try {
      setIsRejecting(true);
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");

      const response = await dispatch(
        rejectBeneficiaryAccount({
          id,
          rejection_reason: declineReason,
        }),
      );

      if (rejectBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account rejected.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
        setRejectPopup(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRejecting(false);
    }
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
    hoverOption: "dark",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
    hoverOption: "dark",
  };

  const buttonRows = [
    ...(canApprove
      ? [
          [
            {
              btnText: "Reject account",
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                if (!isActionInProgress) {
                  setRejectPopup(true);
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isLoaderNeeded: isRejecting,
              isLoading: isRejecting,
              isDisabled: isApproving,
            },
            {
              btnText: "Approve account",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                if (!isActionInProgress) {
                  accountApproval();
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isLoaderNeeded: isApproving,
              isLoading: isApproving,
              isDisabled: isRejecting,
            },
          ],
        ]
      : []),

    [
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_ACCOUNT,
        icon: ICONS?.CREATE_NEW_ACCOUNT,
        handleFunction: () => {
          !isActionInProgress && handlers.createAccount();
        },
        isDisabled: isActionInProgress,
      },
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_TRANSACTION,
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: () => {
          !isActionInProgress && handlers.createTxn();
        },
        isDisabled: !canCreateTxn || isActionInProgress,
      },
      {
        ...primaryBtn,
        btnText: globalText?.VIEW_DETAILS,
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: () => {
          !isActionInProgress && handlers.viewDetails();
        },
        isDisabled: isActionInProgress,
      },
      {
        ...outlineBtn,
        btnText: globalText?.GO_TO_DASHBOARD,
        icon: ICONS?.GO_TO_DASHBOARD,
        handleFunction: () => {
          !isActionInProgress && handlers.dashboard();
        },
        imgColor: "var(--text-primary)",
        isDisabled: isActionInProgress,
      },
    ],
  ];

  const filteredPanAccounts =
    panLookupData?.beneficiary_accounts?.filter((account) =>
      ["pending", "approved"].includes(account?.approval_status?.toLowerCase()),
    ) || [];

  const filteredBankAccounts =
    bankLookupData?.beneficiary_accounts?.filter((account) =>
      ["pending", "approved"].includes(account?.approval_status?.toLowerCase()),
    ) || [];

  useEffect(() => {
    if (beneAccountSuccessData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category_ids,
        pan_no,
        labels,
      } = beneAccountSuccessData;
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        categoryIds: (category_ids || []).map((c) => c.category_id ?? c.id),
        pan: pan_no,
        labels: labels,
      });
    }
  }, [beneAccountSuccessData]);

  useEffect(() => {
    dispatch(beneficiaryAccountDetailsById({ id }));
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (!beneAccountSuccessData) return;

    dispatch(panLookUp({ pan_number: beneAccountSuccessData.pan_no }));

    dispatch(
      bankLookUp({
        ifsc: beneAccountSuccessData.ifsc_code,
        bank_account_no: beneAccountSuccessData.bank_acc_no,
      }),
    );
  }, [beneAccountSuccessData, dispatch]);

  return (
    <div>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopupWithInput
            headerTitle={globalText?.REJECT_ACCOUNT}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            secondHeaderTitle={globalText?.REJECT_POPUP_SUB_TEXT}
            secondHeaderFontWeight="normal"
            secondHeaderFontColor="text-secondary"
            secondHeaderTextSize="font-xs"
            noteText={globalText?.REJECT_POPUP_ACCOUNT_NOTE_TEXT}
            noteColor="text-secondary"
            bulletPoints={[
              globalText.REJECT_POPUP_BULLET_POINT_ONE,
              globalText.REJECT_POPUP_BULLET_POINT_TWO,
              globalText.REJECT_POPUP_BULLET_POINT_THREE,
            ]}
            inputPlaceholder={globalText.REASON}
            inputVariant={computeVariant(declineReason, isFocused)}
            inputOnFocus={(e) => setIsFocused(true)}
            inputOnBlur={(e) => setIsFocused(false)}
            btnOneIsDisabled={isActionInProgress ? true : false}
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              if (!isActionInProgress) {
                handleInputChange(e);
              }
            }}
            inputValue={declineReason}
            inputFontKind="big"
            inputIsFullWidth={true}
            btnOneText="Cancel"
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnTwoText="Reject"
            btnTwoHandleFunction={() => {
              accountRejected();
              setDeclineReason("");
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              declineReason.trim().length <
              GlobalConfiguration.rejectReasonMinLength
            }
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      <StatusContainer
        status="success"
        buttonRows={buttonRows}
        title={
          "Beneficiary account created successfully and awaiting approval."
        }
      >
        <TabsWeapperSection title="Account details">
          <>
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.name,
                  leftSubTitle: "Account name",
                },
              ]}
            />
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: formatAccountNumber(
                    beneAccountSuccessData?.bank_acc_no,
                  ),
                  leftSubTitle: "Account number",
                  ifsc: beneAccountSuccessData?.ifsc_code,
                },
              ]}
            />
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.ifsc_code,
                  leftSubTitle: "IFSC",
                },
              ]}
            />
            {beneAccountSuccessData?.virtual_account === "Y" && (
              <InfoCard
                bgColor="var(--positive-card)"
                rows={[
                  {
                    leftTitle:
                      beneAccountSuccessData?.virtual_account_type === "manual"
                        ? "Manually created"
                        : "Auto detected",
                    leftSubTitle: "Virtual account",
                  },
                ]}
              />
            )}
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.category_ids
                    .map((category) => category?.category_name)
                    .join(", "),
                  leftSubTitle: " Categories",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.pan_no,
                  leftSubTitle: "PAN",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.gstin_detail
                    ? beneAccountSuccessData?.gstin_detail
                    : "Not given",
                  leftSubTitle: "GSTIN (optional)",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: `+91 ${beneAccountSuccessData?.contact_no}`,
                  leftSubTitle: "Contact number",
                  icon: ICONS?.CONTACT_WITH_WHITE_CIRCLE,
                  iconSize: "small",
                  bgNeeded: false,
                },
              ]}
            />
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: linkIdentifier(beneAccountSuccessData?.email_id),
                  leftSubTitle: "Email ID",
                  icon: ICONS?.CONTACT_US_MAIL,
                  iconSize: "small",
                  bgNeeded: false,
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle:
                    Array?.isArray(beneAccountSuccessData?.labels) &&
                    beneAccountSuccessData?.labels?.length >
                      GlobalConfiguration?.lengthCheckZero
                      ? beneAccountSuccessData.labels.map((l) => l).join(", ")
                      : "Not assigned",
                  leftSubTitle: "Labels (optional)",
                },
              ]}
            />
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle:
                    Array.isArray(beneAccountSuccessData?.recipient_email) &&
                    beneAccountSuccessData?.recipient_email.length > 0
                      ? beneAccountSuccessData?.recipient_email.map(
                          (email, id) => (
                            <span key={id}>
                              {linkIdentifier(email)}
                              {id <
                                beneAccountSuccessData?.recipient_email.length -
                                  1 && ", "}
                            </span>
                          ),
                        )
                      : "Not added",
                  leftSubTitle: "Recipients (optional)",
                },
              ]}
            />

            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.id,
                  leftSubTitle: "Account ID",
                  copyText: beneAccountSuccessData?.id,
                },
              ]}
            />
          </>
        </TabsWeapperSection>
        {filteredPanAccounts.length > GlobalConfiguration?.lengthCheckOne && (
          <TabsWeapperSection title="Accounts associated to PAN">
            {filteredPanAccounts.map((accountData, id) => (
              <InfoCard
                key={id}
                bgColor="var(--positive-card)"
                header={true}
                borderColor="var(--positive-bg)"
                rightHeaderText={`${
                  accountData?.approval_status?.charAt(0).toUpperCase() +
                  accountData?.approval_status?.slice(1)
                } account`}
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                leftHeaderText={accountData?.bank_account_holder_full_name}
                leftHeaderWidth="w-width-lg"
                rows={[
                  {
                    leftTitle: formatAccountNumber(accountData?.bank_acc_no),
                    leftSubTitle: "Bank account number",
                    ifsc: accountData?.ifsc_code,
                  },
                ]}
              />
            ))}
          </TabsWeapperSection>
        )}

        {filteredBankAccounts.length > GlobalConfiguration?.lengthCheckOne && (
          <TabsWeapperSection title="Accounts associated to bank">
            {filteredBankAccounts.map((accountData, id) => (
              <InfoCard
                key={id}
                bgColor="var(--positive-card)"
                header={true}
                borderColor="var(--positive-bg)"
                rightHeaderText={`${
                  accountData?.approval_status?.charAt(0).toUpperCase() +
                  accountData?.approval_status?.slice(1)
                } account`}
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                leftHeaderText={accountData?.name}
                leftHeaderWidth="w-width-lg"
                rows={[
                  {
                    leftTitle: accountData?.pan_no,
                    leftSubTitle: "PAN",
                  },
                ]}
              />
            ))}
          </TabsWeapperSection>
        )}
      </StatusContainer>
    </div>
  );
};

export default BeneficiaryAccountSuccess;
